import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Clock, ArrowRight, Globe } from 'lucide-react';
import { useGeoLocation, getRegionInfo } from '@/hooks/useGeoLocation';
import { articles as allArticles } from '@/data/articlesMetadata';

/**
 * Region-to-article slug mapping
 * Maps detected regions to relevant article slugs
 */
const regionArticles = {
    usa: [
        'iptv-usa-streaming-guide',
        'best-iptv-service-2026',
        'iptv-legal-usa',
        'iptv-free-trial-how-to-get-started'
    ],
    uk: [
        'iptv-subscription-what-you-need-to-know',
        'best-iptv-service-2026',
        'live-tv-streaming-service-explained',
        'iptv-provider-comparison'
    ],
    canada: [
        'iptv-canada-best-providers',
        'best-iptv-service-2026',
        'iptv-subscription-what-you-need-to-know',
        'iptv-free-trial-how-to-get-started'
    ],
    australia: [
        'best-iptv-service-2026',
        'iptv-subscription-what-you-need-to-know',
        'live-tv-streaming-service-explained',
        'iptv-provider-comparison'
    ]
};

// Fallback articles (Setup Guides)
const fallbackSlugs = [
    'best-iptv-service-2026',
    'iptv-free-trial-how-to-get-started',
    'iptv-provider-comparison',
    'iptv-subscription-what-you-need-to-know'
];

/**
 * Vertical Blog Card - No featured image, clean minimal design
 */
const VerticalBlogCard = ({ article, index }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
        >
            <Link
                to={`/blog/${article.slug}`}
                className="block glass rounded-xl p-5 h-full hover:border-primary/30 border border-transparent transition-all duration-300 group"
                style={{ boxShadow: 'var(--shadow-glass)' }}
            >
                {/* Category Badge */}
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                        {article.category}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {article.readTime}
                    </span>
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary transition-colors line-clamp-2">
                    {article.title}
                </h3>

                {/* Excerpt */}
                <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                    {article.quickAnswer || article.excerpt}
                </p>

                {/* Read Link */}
                <div className="flex items-center gap-1 text-primary text-sm font-medium group-hover:gap-2 transition-all">
                    Read article
                    <ArrowRight className="w-4 h-4" />
                </div>
            </Link>
        </motion.div>
    );
};

/**
 * SmartRegionalSection Component
 * Shows IP-based personalized blog cards
 */
export const SmartRegionalSection = () => {
    const { country, loading } = useGeoLocation();
    const [displayArticles, setDisplayArticles] = useState([]);
    const [regionInfo, setRegionInfo] = useState(null);

    useEffect(() => {
        // Get region info from country code
        const region = getRegionInfo(country);
        setRegionInfo(region);

        // Get article slugs based on region
        const slugs = region ? regionArticles[region.id] : fallbackSlugs;

        // Find matching articles from metadata
        const matchedArticles = slugs
            .map(slug => allArticles.find(a => a.slug === slug))
            .filter(Boolean)
            .slice(0, 4);

        // If not enough matches, fill with fallback
        if (matchedArticles.length < 4) {
            const needed = 4 - matchedArticles.length;
            const fallbackMatches = fallbackSlugs
                .map(slug => allArticles.find(a => a.slug === slug))
                .filter(a => a && !matchedArticles.includes(a))
                .slice(0, needed);
            matchedArticles.push(...fallbackMatches);
        }

        setDisplayArticles(matchedArticles);
    }, [country]);

    return (
        <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-10 space-y-3"
                >
                    <div className="flex items-center justify-center gap-3">
                        {regionInfo ? (
                            <>
                                <MapPin className="w-6 h-6 text-primary" />
                                <div className="flex items-center gap-3">
                                    <img
                                        src={`https://flagcdn.com/w80/${country.toLowerCase()}.png`}
                                        alt={regionInfo.name}
                                        className="h-8 w-auto object-contain rounded shadow-sm"
                                    />
                                    <h2 className="text-3xl sm:text-4xl font-bold">
                                        We are available in {regionInfo.name}
                                    </h2>
                                </div>
                            </>
                        ) : (
                            <>
                                <Globe className="w-6 h-6 text-primary" />
                                <h2 className="text-3xl sm:text-4xl font-bold">
                                    Popular Guides
                                </h2>
                            </>
                        )}
                    </div>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                        {regionInfo
                            ? `Check out our IPTV ${regionInfo.demonym} Knowledge Hub`
                            : 'Essential guides to get started with IPTV'}
                    </p>
                </motion.div>

                {/* Loading State */}
                {
                    loading && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="glass rounded-xl p-5 h-48 animate-pulse">
                                    <div className="h-4 bg-white/10 rounded w-1/3 mb-3"></div>
                                    <div className="h-6 bg-white/10 rounded w-full mb-2"></div>
                                    <div className="h-4 bg-white/10 rounded w-2/3"></div>
                                </div>
                            ))}
                        </div>
                    )
                }

                {/* Article Cards Grid */}
                {
                    !loading && displayArticles.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {displayArticles.map((article, index) => (
                                <VerticalBlogCard key={article.slug} article={article} index={index} />
                            ))}
                        </div>
                    )
                }

                {/* View All Link */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="text-center mt-8"
                >
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
                    >
                        View all articles
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </motion.div>
            </div >
        </section >
    );
};
